/**
 * The Fleet screen (M6 plan P28, spec §8): every ship with class, captain, order, location, status, cargo and
 * earnings yesterday and today (the P&L's basis) in a windowed list, the filters, the sort and bulk preset orders. It renders a
 * `FleetScreenState` and dispatches `FleetAction`s; all logic is in `fleetModel.ts`. The focused control or row
 * carries `data-focused` (keyboard and pad focus are the model's, not the DOM's). UI events only.
 */
import type { ReactNode } from 'react'
import {
  BAR,
  creditsLabel,
  FILTER_KEYS,
  FILTER_LABELS,
  filterValueText,
  SORT_KEYS,
  SORT_LABELS,
  type BarControl,
  type FleetAction,
  type FleetRow,
  type FleetScreenConfig,
  type FleetScreenState,
  type PresetInfo,
  type SortKey,
} from './fleetModel'
import { WindowedList } from './WindowedList'
import '../strategic/strategic.css'
import './fleet.css'

export interface FleetProps {
  state: FleetScreenState
  presets: readonly PresetInfo[]
  config: FleetScreenConfig
  dispatch: (a: FleetAction) => void
  /** The fleet cap (FleetView.cap), shown beside the count. */
  cap?: number
  loading?: boolean
  /** True while the ship detail is open over the screen: its own key hint replaces this one (D-459 nit). */
  covered?: boolean
}

function focusAttr(on: boolean): { 'data-focused'?: '' } {
  return on ? { 'data-focused': '' } : {}
}

function controlLabel(s: FleetScreenState, c: BarControl, presets: readonly PresetInfo[]): string {
  switch (c) {
    case 'filter:shipClass':
    case 'filter:order':
    case 'filter:status':
    case 'filter:system': {
      const key = FILTER_KEYS[BAR.indexOf(c)]
      return `${FILTER_LABELS[key]}: ${filterValueText(s.rows, key, s.filter[key])}`
    }
    case 'sort':
      return `Sort: ${SORT_LABELS[s.sort.key]}`
    case 'sortDir':
      return s.sort.desc ? '▼ High first' : '▲ Low first'
    case 'preset':
      return `Preset: ${presets[s.preset]?.name ?? '—'}`
    case 'apply':
      return `Apply to selected (${s.selected.size})`
    case 'selectAll':
      return 'Select all shown'
    case 'clear':
      return 'Clear selection'
  }
}

const COLUMNS: readonly SortKey[] = SORT_KEYS

function cell(r: FleetRow, key: SortKey): string {
  switch (key) {
    case 'name':
      return r.name
    case 'shipClass':
      return r.classLabel
    case 'captain':
      return r.captain ?? 'No captain'
    case 'order':
      return r.orderLabel
    case 'location':
      return r.location
    case 'status':
      return r.statusLabel
    case 'cargo':
      return r.cargo
    case 'yesterday':
      return creditsLabel(r.earnedYesterday)
    case 'today':
      return creditsLabel(r.earnedToday)
  }
}

function negative(r: FleetRow, key: SortKey): boolean {
  return (key === 'yesterday' && Math.round(r.earnedYesterday) < 0) || (key === 'today' && Math.round(r.earnedToday) < 0)
}

export function Fleet(p: FleetProps): ReactNode {
  const s = p.state
  const listFocused = s.focus === 'list'
  const list = p.config.list
  const sortMark = (k: SortKey): string => (s.sort.key === k ? (s.sort.desc ? ' ▼' : ' ▲') : '')

  function row(i: number): ReactNode {
    const r = s.view[i]
    const selected = s.selected.has(r.id)
    const cursor = listFocused && i === s.cursor
    return (
      <div
        className="fl-row"
        data-testid="fleet-row"
        data-ship={r.id}
        data-selected={selected ? '' : undefined}
        data-status={r.status}
        {...focusAttr(cursor)}
        onClick={() => p.dispatch({ type: 'cursor', index: i })}
      >
        <button
          type="button"
          tabIndex={-1}
          className="sui-btn fl-check"
          aria-pressed={selected}
          aria-label={`Select ${r.name}`}
          data-testid="fleet-select"
          onClick={(e) => {
            e.stopPropagation()
            p.dispatch({ type: 'toggle', id: r.id })
          }}
        >
          {selected ? '☑' : '☐'}
        </button>
        {COLUMNS.map((k) =>
          k === 'name' ? (
            <button
              key={k}
              type="button"
              tabIndex={-1}
              className="sui-btn fl-cell fl-name"
              data-testid="fleet-open"
              title="Ship detail"
              onClick={(e) => {
                e.stopPropagation()
                p.dispatch({ type: 'open', id: r.id })
              }}
            >
              {r.name}
            </button>
          ) : (
            <span key={k} className={`fl-cell fl-${k}`} data-negative={negative(r, k) ? '' : undefined}>
              {cell(r, k)}
            </span>
          ),
        )}
      </div>
    )
  }

  return (
    <div className="sui fl-screen" data-testid="fleet-screen">
      <header className="sui-head">
        <span className="fl-count" data-testid="fleet-count">
          {s.view.length === s.rows.length ? `${s.rows.length}` : `${s.view.length} of ${s.rows.length}`} ships
          {p.cap !== undefined ? ` · cap ${p.cap}` : ''} · {s.selected.size} selected
        </span>
      </header>
      <div className="fl-bar" role="toolbar" aria-label="Fleet filters, sort and orders">
        {BAR.map((c) => (
          <button
            key={c}
            type="button"
            tabIndex={-1}
            className={`sui-btn fl-ctl fl-ctl--${c.replace(':', '-')}`}
            data-testid={`fleet-${c.replace(':', '-')}`}
            {...focusAttr(s.focus === c)}
            onClick={() => p.dispatch({ type: 'press', control: c })}
          >
            {controlLabel(s, c, p.presets)}
          </button>
        ))}
      </div>
      <p className="fl-notice" role="status" aria-live="polite" data-testid="fleet-notice">
        {s.notice ?? (p.loading === true ? 'Reading the fleet…' : '')}
      </p>
      <div className="fl-row fl-headrow" role="row">
        <span className="fl-check" />
        {COLUMNS.map((k) => (
          <button key={k} type="button" tabIndex={-1} className={`sui-btn fl-cell fl-th fl-${k}`} data-testid={`fleet-col-${k}`} onClick={() => p.dispatch({ type: 'sortBy', key: k })}>
            {SORT_LABELS[k]}
            {sortMark(k)}
          </button>
        ))}
      </div>
      <WindowedList
        count={s.view.length}
        rowHeight={list.rowHeight}
        visibleRows={list.visibleRows}
        overscan={list.overscan}
        first={s.first}
        renderRow={row}
        onScroll={(first) => p.dispatch({ type: 'scroll', first })}
        className="fl-list"
        testId="fleet-list"
        label="Ships"
        empty={<p className="fl-empty">{s.rows.length === 0 ? 'You own no ships yet. Buy one at a shipyard.' : 'No ship matches these filters.'}</p>}
      />
    </div>
  )
}
